import { useState, useCallback } from 'react';
import { Upload, X, File } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../../../components/ui/Dialog';
import { Button } from '../../../../components/ui/Button';

// ----------------------------------------------------------------------

interface FileNewFolderDialogWithFilterProps {
  open: boolean;
  busy?: boolean;
  supportedExtensionsDesc?: string;
  supportedExtensionsRegEx?: RegExp;
  fileCountLimit?: number;
  onClose: () => void;
  onUpload: (files: (File | string)[]) => Promise<void>;
}

export default function FileNewFolderDialogWithFilter({
  open,
  busy = false,
  supportedExtensionsDesc = 'All file types accepted',
  supportedExtensionsRegEx,
  fileCountLimit = 1,
  onClose,
  onUpload
}: FileNewFolderDialogWithFilterProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleFiles = useCallback(
    (newFiles: File[]) => {
      setError(null);

      // Check file count limit
      setFiles((prev) => {
        if (prev.length + newFiles.length > fileCountLimit) {
          setError(`Maximum ${fileCountLimit} file(s) allowed`);
          return prev;
        }

        // Filter by extension if provided
        const validFiles = newFiles.filter((file) => {
          if (!supportedExtensionsRegEx) return true;
          return supportedExtensionsRegEx.test(file.name);
        });

        if (validFiles.length !== newFiles.length) {
          setError(`Some files were rejected. ${supportedExtensionsDesc}`);
        }

        return [...prev, ...validFiles].slice(0, fileCountLimit);
      });
    },
    [fileCountLimit, supportedExtensionsRegEx, supportedExtensionsDesc]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const droppedFiles = Array.from(event.dataTransfer.files);
      handleFiles(droppedFiles);
    },
    [handleFiles]
  );

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files ? Array.from(event.target.files) : [];
    handleFiles(selectedFiles);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    await onUpload(files);
    setFiles([]);
    onClose();
  };

  const handleClose = () => {
    setFiles([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload File</DialogTitle>
          <DialogDescription>{supportedExtensionsDesc}</DialogDescription>
        </DialogHeader>

        <div
          className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground">
            Drag and drop files here, or click to select
          </p>
          <input
            id="file-input"
            type="file"
            className="hidden"
            onChange={handleFileInput}
            multiple={fileCountLimit > 1}
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {files.length > 0 && (
          <div className="space-y-2">
            {files.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                <div className="flex items-center gap-2">
                  <File className="h-4 w-4" />
                  <span className="text-sm truncate">{file.name}</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => removeFile(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={busy || files.length === 0}>
            {busy ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
